import React, { ReactElement, useEffect, useId, useRef, useState } from 'react'
import Header from 'src/components/Header/Header'

import CardActionsMenu from './CardActionsMenu/CardActionsMenu'
import CardArt from './CardArt/CardArt'
import * as style from './CardDetailHero.style'
import CardDisplayActions from './CardDisplayActions/CardDisplayActions'
import CardHeroCompact from './CardHeroCompact/CardHeroCompact'
import CardRevealPanel from './CardRevealPanel/CardRevealPanel'
import CardSummary from './CardSummary/CardSummary'
import { useCardDetailHeroData } from './useCardDetailHeroData'
import { useCardRevealDisclosure } from './useCardRevealDisclosure'
import { useCompactHeroSwap } from './useCompactHeroSwap'

type CardDetailHeroProps = {
  /**
   * Runtime banner offset, read from redux by the page (the feature stays
   * redux-free). Drives both the swap threshold and the pinned compact bar's
   * fixed `top`, so a banner opening shifts them together.
   */
  fixedElementPushdown: number
  /**
   * Whether the sidenav is locked open, also read from redux by the page. Lets
   * the pinned compact bar inset from the left by the sidenav's reserved width
   * so its leading content is not hidden behind the fixed sidenav.
   */
  sideNavLockedOpen: boolean
}

const CardDetailHero: React.FC<CardDetailHeroProps> = ({
  fixedElementPushdown,
  sideNavLockedOpen,
}): ReactElement => {
  const { card, billing, credentials } = useCardDetailHeroData()

  // The full hero's reveal-panel id. Per-instance (`useId`, review finding #10)
  // rather than a module constant: the compact bar mounts its own panel with its
  // own id, so neither Show details trigger's aria-controls can resolve to the
  // wrong panel while both heroes are mounted.
  const revealPanelId = useId()

  // The shell owns card status so the summary pill, the card-art overlay, and
  // the freeze button label always agree — the design spec's "one owner" rule.
  // Demo-stub: Freeze toggles active⇄frozen locally; issuer-imposed LOCKED is
  // left unchanged (its action is "Request unlock", not a voluntary freeze).
  const [status, setStatus] = useState(card.status)
  const toggleFreeze = (): void =>
    setStatus((current) =>
      current === 'ACTIVE'
        ? 'FROZEN'
        : current === 'FROZEN'
          ? 'ACTIVE'
          : current,
    )

  // The shell also owns the reveal disclosure (same "one owner" rule); both the
  // full hero and the compact bar share it, anchored to whichever opened it.
  const reveal = useCardRevealDisclosure()
  const { close: closeReveal } = reveal

  // The full→compact swap: observe the full hero's bottom edge and flip once it
  // passes under the global header. Both heroes stay mounted; the off-screen one
  // is inert.
  const fullHeroRef = useRef<HTMLElement>(null)
  const compactOpen = useCompactHeroSwap(fullHeroRef, fixedElementPushdown)

  // Close the reveal across any swap so a panel is never left anchored to a
  // now-inert hero (plan §3 rework #2 / §5). `closeReveal` is stable.
  useEffect(() => {
    closeReveal()
  }, [compactOpen, closeReveal])

  return (
    // Reuse the legacy Header layout so the hero occupies the exact page band
    // (white background + standard responsive header padding) the AccountHeader
    // it replaces did — keeping horizontal alignment and the gap to the Subnav
    // below identical across breakpoints, rather than re-deriving that padding.
    <Header>
      <section
        ref={fullHeroRef}
        css={style.hero}
        data-testid="card-detail-hero"
        aria-hidden={compactOpen}
        // Once the compact bar takes over, the full hero has scrolled under the
        // global header; make it inert so its (now duplicated) controls leave the
        // a11y tree and tab order while it stays mounted for the jank-free swap.
        {...(compactOpen ? { inert: '' } : {})}
      >
        <div css={style.left} data-testid="card-detail-hero-left">
          <CardSummary status={status} />
        </div>
        <div
          css={style.right}
          data-testid="card-detail-hero-right"
          ref={reveal.containerRefFor('full')}
        >
          <div css={style.toolbar} data-testid="card-detail-hero-toolbar">
            <CardDisplayActions
              status={status}
              onFreeze={toggleFreeze}
              revealOpen={reveal.isOpenFor('full')}
              onToggleReveal={() => reveal.toggle('full')}
              revealPanelId={revealPanelId}
            />
            {/*
              Ungated: no `capabilities` passed → all five actions show. When the
              real CapabilityDecisions endpoint lands, pass the per-action verdict
              here. Do not branch on role names — the server is the sole authority.
            */}
            <CardActionsMenu />
          </div>
          <CardArt
            status={status}
            network={card.network}
            last4={card.last4}
            holder={card.cardholder.displayName}
            pan={credentials.pan}
            exp={credentials.exp}
            revealed={reveal.isOpenFor('full')}
          />
          {reveal.isOpenFor('full') && (
            <CardRevealPanel
              id={revealPanelId}
              cardholderName={card.cardholder.displayName}
              address={billing.address}
              pan={credentials.pan}
              cvc={credentials.cvc}
            />
          )}
        </div>
      </section>

      {/*
        The pinned condensed hero. Always mounted (inert until the swap opens it),
        so its ids are unique per variant and its reveal is a distinct anchor. It
        is `position: fixed`, rendered as a sibling of the full hero.
      */}
      <CardHeroCompact
        open={compactOpen}
        fixedElementPushdown={fixedElementPushdown}
        sideNavLockedOpen={sideNavLockedOpen}
        status={status}
        onFreeze={toggleFreeze}
        revealOpen={reveal.isOpenFor('compact')}
        onToggleReveal={() => reveal.toggle('compact')}
        revealContainerRef={reveal.containerRefFor('compact')}
      />
    </Header>
  )
}

export default CardDetailHero
